/*
 * sh-bundle-color.js
 * Adds a colour selector to each slot of the Section Heroes bundle.
 * Colours are separate products (metafield custom.products_color), not variants,
 * so the app never shows them. This script injects the picker, keeps sizes in
 * sync with stock, and rewrites the /cart/add payload to the chosen colour's
 * variant while preserving the discount marker (_sh-bundle).
 */
(function () {
  'use strict';

  var LABEL = 'Farbe';
  var maps = null;

  // 1. Rewrite /cart/add to the chosen colour's variant.
  // The fetch wrapper is installed early by snippets/sh-bundle-cart-intercept.liquid
  // (in <head>, before content_for_header) so it beats cart apps (UpCart) that cache
  // native fetch first. Here we just supply the rewrite it delegates to.
  var origFetch = window.__shOrigFetch || window.fetch;

  window.__shBundleRewrite = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (url.indexOf('/cart/add') === -1) return origFetch.call(this, input, init);

    var self = this;
    return (async function () {
      try {
        var isReq = (typeof Request !== 'undefined') && (input instanceof Request);
        var body = (init && init.body != null)
          ? init.body
          : (isReq ? await input.clone().text() : null);

        if (typeof body === 'string') {
          var clean = buildCleanAdd(body);
          if (clean) {
            var headers = Object.assign({}, (init && init.headers) || {}, {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            });
            var newInit = Object.assign({}, init || {}, { method: 'POST', body: clean.body, headers: headers });
            var addInput = isReq ? url : input;

            var resp = await origFetch.call(self, addInput, newInit);

            // /cart/add.js ignores cart attributes — preserve them (e.g. _Foxify) separately.
            if (clean.attributes && Object.keys(clean.attributes).length) {
              try {
                var updateUrl = url.split('?')[0].replace('/cart/add', '/cart/update');
                await origFetch.call(self, updateUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ attributes: clean.attributes })
                });
              } catch (e2) { /* best-effort attribute sync */ }
            }

            return resp;
          }
        }
      } catch (e) {
        console.warn('[sh-bundle-color] cart intercept skipped:', e);
      }
      return origFetch.call(self, input, init);
    })();
  };

  // Fallback: patch fetch here if the early <head> hook is missing (may miss apps
  // that cache fetch before this runs).
  if (!window.__shOrigFetch && typeof window.fetch === 'function') {
    var nativeFetch = window.fetch;
    window.__shOrigFetch = nativeFetch;
    origFetch = nativeFetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1) return window.__shBundleRewrite.call(this, input, init);
      return nativeFetch.apply(this, arguments);
    };
  }

  // Rebuild the add payload as one line per slot, keeping each line's _sh-bundle
  // property. Returns { body, attributes } or null when it is not a bundle add.
  function buildCleanAdd(str) {
    var data;
    try { data = JSON.parse(str); } catch (e) { return null; }
    if (!data || !Array.isArray(data.items) || !data.items.length) return null;

    var it = data.items[0];
    if (!it || typeof it !== 'object' || !('id' in it)) return null;

    var isBundle = ('properties[_sh-bundle]' in it)
      || (it.properties && it.properties['_sh-bundle'])
      || Object.keys(it).some(function (k) { return k.indexOf('shopify-additional-items[') === 0; });
    if (!isBundle) return null;

    var bundle = findSubmittingBundle(it);
    if (!bundle) return null;
    var map = getMapForBundle(bundle);
    if (!map) return null;
    var block = getSelectedBlock(bundle);
    if (!block) return null;
    var slots = getSlots(block);
    if (!slots.length) return null;

    var lines = getIncomingLines(data);

    var items = [];
    slots.forEach(function (slot, i) {
      var line = lines[i] || lines[0] || {};
      var id = resolveVariantId(slot, map);
      if (!id) id = line.id; // fall back to the app's own id for this slot
      if (!id) return;
      items.push({ id: Number(id), quantity: parseInt(line.quantity, 10) || 1, properties: line.properties || {} });
    });
    if (!items.length) return null;

    return { body: JSON.stringify({ items: items }), attributes: extractAttrs(it) };
  }

  // Normalize the body into one {id, quantity, properties} per line — handles both the
  // app's packed items[0] (shopify-additional-items[...]) and a clean array (UpCart).
  function getIncomingLines(data) {
    var it0 = data.items[0];
    var packed = Object.keys(it0).some(function (k) { return k.indexOf('shopify-additional-items[') === 0; });

    if (data.items.length > 1 && !packed) {
      return data.items.map(function (li) {
        return {
          id: li.id,
          quantity: li.quantity,
          properties: (li.properties && typeof li.properties === 'object') ? li.properties : {}
        };
      });
    }

    var lines = [{ id: it0.id, quantity: it0.quantity, properties: extractProps(it0, 0) }];
    var i = 1;
    while (('shopify-additional-items[' + i + '][id]') in it0) {
      lines.push({
        id: it0['shopify-additional-items[' + i + '][id]'],
        quantity: it0['shopify-additional-items[' + i + '][quantity]'],
        properties: extractProps(it0, i)
      });
      i++;
    }
    return lines;
  }

  // Line-item properties for slot i from the serialized form keys.
  function extractProps(it, i) {
    var props = {};
    if (i === 0) {
      if (it.properties && typeof it.properties === 'object') {
        for (var k in it.properties) { if (Object.prototype.hasOwnProperty.call(it.properties, k)) props[k] = it.properties[k]; }
      }
      Object.keys(it).forEach(function (key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = it[key];
      });
    } else {
      var prefix = 'shopify-additional-items[' + i + '][properties][';
      Object.keys(it).forEach(function (key) {
        if (key.indexOf(prefix) === 0 && key.charAt(key.length - 1) === ']') {
          props[key.slice(prefix.length, -1)] = it[key];
        }
      });
    }
    return props;
  }

  // Cart-level attributes (attributes[...]) from the serialized form keys.
  function extractAttrs(it) {
    var attrs = {};
    Object.keys(it).forEach(function (key) {
      var m = key.match(/^attributes\[(.+)\]$/);
      if (m) attrs[m[1]] = it[key];
    });
    return attrs;
  }

  // Which <sh-bundle> produced this payload? Match the map that owns the main variant id.
  function findSubmittingBundle(item) {
    var bundles = document.querySelectorAll('sh-bundle');
    if (bundles.length === 1) return bundles[0];
    var id = String(item.id);
    for (var i = 0; i < bundles.length; i++) {
      var m = getMapForBundle(bundles[i]);
      if (m && mapHasVariant(m, id)) return bundles[i];
    }
    return bundles[0] || null;
  }

  // 2. Colour data maps
  function loadMaps() {
    if (maps) return maps;
    maps = [];
    document.querySelectorAll('script[data-sh-bundle-color-map]').forEach(function (el) {
      try {
        var m = JSON.parse(el.textContent);
        if (m && Array.isArray(m.colors)) {
          maps.push(m);
          m.colors.forEach(function (c) { if (c.image) { var im = new Image(); im.src = c.image; } }); // warm cache
        }
      } catch (e) { /* ignore malformed island */ }
    });
    return maps;
  }

  function mapHasVariant(map, id) {
    id = String(id);
    return map.colors.some(function (c) {
      return Object.keys(c.sizes || {}).some(function (s) {
        return String(c.sizes[s].id) === id;
      });
    });
  }

  function getMapForBundle(bundle) {
    var all = loadMaps();
    if (!all.length) return null;

    // Prefer the map that contains the bundle's default variant id.
    var defBlock = bundle.querySelector('.sh-bundle-block[data-variant-id]');
    var defId = defBlock ? defBlock.getAttribute('data-variant-id') : null;
    if (defId) {
      for (var i = 0; i < all.length; i++) {
        if (mapHasVariant(all[i], defId)) return all[i];
      }
    }
    // Fallback: match by handle (no blind single-map fallback — would mismatch cross-page bundles).
    var handle = bundle.getAttribute('data-product-handle');
    for (var j = 0; j < all.length; j++) {
      if (all[j].productHandle && all[j].productHandle === handle) return all[j];
    }
    return null;
  }

  // 3. Selected offer + its slots
  function getSelectedBlock(bundle) {
    var sel = bundle.querySelector('.sh-bundle-block-content.selected');
    if (sel) {
      var b = sel.closest('.sh-bundle-block');
      if (b) return b;
    }
    var blocks = bundle.querySelectorAll('.sh-bundle-block');
    for (var i = 0; i < blocks.length; i++) {
      var r = blocks[i].querySelector('input[name="sh-bundle-offer"]');
      if (r && r.checked) return blocks[i];
    }
    return blocks[0] || null;
  }

  function getSlots(block) {
    var rows = Array.prototype.slice.call(block.querySelectorAll('.sh-bundle-variant-picker-row'));
    rows.sort(function (a, b) {
      return (parseInt(a.getAttribute('data-slot-index'), 10) || 0)
           - (parseInt(b.getAttribute('data-slot-index'), 10) || 0);
    });
    return rows;
  }

  function getSizeSelect(slot) { return slot.querySelector('select[data-option-name]'); }
  function getColorSelect(slot) { return slot.querySelector('select[data-sh-color-select]'); }

  // Resolve a slot's chosen colour+size to its size entry { id, available, price }.
  function resolveEntry(slot, map) {
    // Trust slotState first — it survives the app re-rendering rows at add-to-cart time.
    var color = slotState[slotKey(slot)];
    if (!color) {
      var cs = getColorSelect(slot);
      color = cs ? cs.value : null;
    }
    if (!color) color = map.colors[0] && map.colors[0].label;

    var ss = getSizeSelect(slot);
    var size = ss ? ss.value : null;
    if (!size) return null;

    var c = null;
    for (var i = 0; i < map.colors.length; i++) {
      if (map.colors[i].label === color) { c = map.colors[i]; break; }
    }
    if (!c) c = map.colors[0];

    return c && c.sizes ? (c.sizes[size] || null) : null;
  }

  function resolveVariantId(slot, map) {
    var e = resolveEntry(slot, map);
    return e ? e.id : null;
  }

  // 4. Inject the colour dropdowns
  function enhanceAll() {
    document.querySelectorAll('sh-bundle').forEach(function (bundle) {
      var map = getMapForBundle(bundle);
      if (!map || !map.colors || map.colors.length < 2) return; // nothing to choose
      Array.prototype.slice
        .call(bundle.querySelectorAll('.sh-bundle-variant-picker-row'))
        .forEach(function (slot) { ensureColor(slot, map); });
      Array.prototype.slice
        .call(bundle.querySelectorAll('.sh-bundle-block'))
        .forEach(function (block) {
          if (block.querySelector('.sh-bundle-variant-picker-row')) recalcPrices(block, map);
        });
    });
  }

  // Remembers each slot's chosen colour so it survives the app re-rendering the rows.
  var slotState = {};

  function slotKey(slot) {
    var block = slot.closest('.sh-bundle-block');
    var offer = block ? (block.getAttribute('data-offer-id') || block.getAttribute('data-offer-index') || '') : '';
    return offer + ':' + (slot.getAttribute('data-slot-index') || '0');
  }

  // Idempotent: injects the colour <select> if missing and re-applies the remembered
  // colour, so it wins even when the app re-renders rows on a size change.
  function ensureColor(slot, map) {
    var container = slot.querySelector('.sh-bundle-block-content-item-product-variants');
    if (!container) return;

    var key = slotKey(slot);
    var saved = slotState[key];
    if (!map.colors.some(function (c) { return c.label === saved; })) {
      saved = map.colors[0].label;
      slotState[key] = saved;
    }

    var sel = container.querySelector('[data-sh-color-select]');
    if (!sel) {
      var opt = document.createElement('div');
      opt.className = 'sh-bundle-option sh-bundle-option--color';

      var label = document.createElement('label');
      label.textContent = LABEL + ':';

      var selWrap = document.createElement('div');
      selWrap.className = 'sh-bundle-select-wrapper';

      sel = document.createElement('select');
      sel.setAttribute('data-sh-color-select', '');

      map.colors.forEach(function (c) {
        var o = document.createElement('option');
        o.value = c.label;
        o.textContent = c.label;
        sel.appendChild(o);
      });

      selWrap.appendChild(sel);
      opt.appendChild(label);
      opt.appendChild(selWrap);
      container.insertBefore(opt, container.firstChild);

      sel.addEventListener('change', function (e) {
        e.stopPropagation(); // keep the app's delegated listeners off the colour dropdown
        slotState[slotKey(slot)] = sel.value;
        syncSizes(slot, map);
        swapImage(slot, map);
        var blk = slot.closest('.sh-bundle-block');
        if (blk) recalcPrices(blk, map);
      });
    }

    if (sel.value !== saved) sel.value = saved; // enforce the remembered colour
    syncSizes(slot, map);
    swapImage(slot, map);
  }

  function currentColor(slot, map) {
    var cs = getColorSelect(slot);
    var val = cs ? cs.value : null;
    for (var i = 0; i < map.colors.length; i++) {
      if (map.colors[i].label === val) return map.colors[i];
    }
    return map.colors[0];
  }

  // Enable only the sizes the chosen colour stocks; re-pick if the current one is gone.
  function syncSizes(slot, map) {
    var ss = getSizeSelect(slot);
    var c = currentColor(slot, map);
    if (!ss || !c) return;

    var curValid = false;
    Array.prototype.forEach.call(ss.options, function (o) {
      var e = c.sizes[o.value];
      var ok = !!(e && e.available);
      o.disabled = !ok;
      if (ok && o.value === ss.value) curValid = true;
    });

    if (!curValid) {
      var first = Array.prototype.filter.call(ss.options, function (o) { return !o.disabled; })[0];
      if (first) {
        ss.value = first.value;
        ss.dispatchEvent(new Event('change', { bubbles: true })); // let the app re-read
      }
    }
  }

  function swapImage(slot, map) {
    var c = currentColor(slot, map);
    if (!c || !c.image) return;
    var img = slot.querySelector('[data-prefix-image] img');
    if (img && img.getAttribute('src') !== c.image) img.src = c.image;
  }

  // Recompute the offer total from the chosen colours. Colours are separate products
  // with their own prices, which the app's base-price total ignores — so the widget
  // would under/over-state the price for pricier colours. We sum the real variant
  // prices and re-apply the app's own discount factor. Idempotent (only writes on change).
  function recalcPrices(block, map) {
    var totalEl = block.querySelector('.sh-bundle-block-content-item-price-total');
    if (!totalEl) return;
    var compareEl = block.querySelector('.sh-bundle-block-content-item-price-compareAt');

    var slots = getSlots(block);
    if (!slots.length) return;

    var sum = 0; // regular price sum, in cents
    for (var i = 0; i < slots.length; i++) {
      var e = resolveEntry(slots[i], map);
      if (!e || typeof e.price !== 'number') return; // missing price → leave the app value
      sum += e.price;
    }

    // Mirror Shopify's automatic discount: round the DISCOUNT, then subtract — not
    // round(total) — so the widget matches the cart to the cent.
    var pct = 1 - readFactor(block);
    var totalStr = formatMoney(sum - Math.round(sum * pct));
    if (totalEl.textContent.trim() !== totalStr) totalEl.textContent = totalStr;
    if (compareEl) {
      var cmpStr = formatMoney(sum);
      if (compareEl.textContent.trim() !== cmpStr) compareEl.textContent = cmpStr;
    }
  }

  // Discount factor (e.g. 0.9 for "-10%") from the app's badge, else its shown
  // total/compare ratio, else 1.
  function readFactor(block) {
    var dEl = block.querySelector('.sh-bundle-block-content-item-price-discount');
    if (dEl) {
      var m = dEl.textContent.match(/(\d+(?:[.,]\d+)?)\s*%/);
      if (m) {
        var pct = parseFloat(m[1].replace(',', '.'));
        if (pct >= 0 && pct < 100) return 1 - pct / 100;
      }
    }
    var t = parseMoney(block.querySelector('.sh-bundle-block-content-item-price-total'));
    var c = parseMoney(block.querySelector('.sh-bundle-block-content-item-price-compareAt'));
    if (t != null && c && c > 0) return t / c;
    return 1;
  }

  // "1.389,85 €" -> 138985 (cents); null if unparseable.
  function parseMoney(el) {
    if (!el) return null;
    var s = el.textContent.replace(/[^\d.,]/g, '').replace(/\./g, '').replace(',', '.');
    var n = parseFloat(s);
    return isNaN(n) ? null : Math.round(n * 100);
  }

  // 138985 (cents) -> "1.389,85 €"
  function formatMoney(cents) {
    return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  // 5. Boot + stay resilient to the app re-rendering slots
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var run = function () { scheduled = false; enhanceAll(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }

  function boot() {
    enhanceAll();
    if (document.body) {
      // characterData too: the app repaints prices via text-node updates, and we must
      // re-run recalcPrices after it so our real-colour total wins.
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
